import type { LinkInfo, LinkState, SessionView } from "@gsignatures/core";
import type { SignerApi } from "../api/types";
import { Icon, type IconName } from "../components/Icon";
import { longDate, mailto, who } from "../lib/format";
import { useFocusHeading } from "../lib/hooks";
import { S } from "../strings";

type Shown = Exclude<LinkState, "ready">;

const LOOK: Record<Shown, { icon: IconName; tone: string }> = {
  expired: { icon: "clock", tone: "flag" },
  paused: { icon: "lock", tone: "err" },
  completed: { icon: "check", tone: "ok" },
  voided: { icon: "x", tone: "" },
  declined: { icon: "x", tone: "" },
  paper: { icon: "paper", tone: "" },
};

interface Props {
  api: SignerApi;
  link: LinkInfo;
  /** Present when this browser is verified (paper: lists the packet to download). */
  session: SessionView | null;
  onVerify: () => void;
}

/** The calm pages for a link that isn't open for signing: each says what happened and what, if anything, to do. */
export function StateScreen({ api, link, session, onVerify }: Props) {
  const heading = useFocusHeading(link.state);
  const w = who(link.sender);
  const kind = link.state as Shown;
  const look = LOOK[kind];
  let title = "";
  let text = "";
  let actions: React.ReactNode = null;

  switch (kind) {
    case "expired":
      title = S.states.expired.title;
      text = S.states.expired.text(w);
      actions = (
        <a className="btn btn-primary btn-wide" href={mailto(w.contactEmail, S.states.expired.mailSubject)}>
          <Icon n="mail" /> {S.states.expired.cta}
        </a>
      );
      break;
    case "paused":
      title = S.states.paused.title;
      text = S.states.paused.text(w);
      actions = (
        <a className="btn btn-quiet btn-wide" href={mailto(w.contactEmail)}>
          <Icon n="mail" /> {S.states.paused.cta(w)}
        </a>
      );
      break;
    case "completed":
      title = S.states.completed.title;
      text = S.states.completed.text(link.completedAt ? longDate(link.completedAt) : null);
      actions = (
        <button type="button" className="btn btn-primary btn-wide" onClick={onVerify}>
          <Icon n="download" /> {S.states.completed.cta}
        </button>
      );
      break;
    case "voided":
      title = S.states.voided.title(w);
      text = S.states.voided.text(w);
      break;
    case "declined":
      title = S.states.declined.title;
      text = S.states.declined.text(w);
      break;
    case "paper":
      title = S.states.paper.title;
      text = S.states.paper.text(w);
      actions = (
        <>
          {session ? (
            <ul className="doc-list compact">
              {session.documents.map((d) => (
                <li className="doc-row" key={d.id}>
                  <div className={`thumb ${d.kind === "form" ? "form" : ""}`} aria-hidden="true" />
                  <div>
                    <b>{d.title}</b>
                  </div>
                  <a className="btn btn-quiet" href={api.fileUrl(d.id)} download target="_blank" rel="noreferrer" aria-label={`${S.done.download} ${d.title}`}>
                    <Icon n="download" /> {S.done.download}
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <button type="button" className="btn btn-primary btn-wide" onClick={onVerify}>
              <Icon n="download" /> {S.states.paper.verifyCta}
            </button>
          )}
          <a className="btn btn-quiet btn-wide" href={mailto(w.contactEmail, S.states.paper.mailSubject)}>
            <Icon n="mail" /> {S.states.paper.email(w)}
          </a>
        </>
      );
      break;
  }

  return (
    <main className="page" id="main">
      <div className="panel panel-pad center-card">
        <div className={`badge-ico ${look.tone}`}>
          <Icon n={look.icon} />
        </div>
        <h1 className="title" ref={heading} tabIndex={-1}>
          {title}
        </h1>
        <p className="lede">{text}</p>
        {actions && <div className="stack state-actions">{actions}</div>}
      </div>
    </main>
  );
}

export function DeadLinkScreen() {
  const heading = useFocusHeading();
  return (
    <main className="page" id="main">
      <div className="panel panel-pad center-card">
        <div className="badge-ico muted-ico">
          <Icon n="lock" />
        </div>
        <h1 className="title" ref={heading} tabIndex={-1}>
          {S.dead.title}
        </h1>
        <p className="lede">{S.dead.text}</p>
      </div>
    </main>
  );
}
